import { StoreIdEntry, StoreIdType } from '../models/PlaceService.ts';
import { ServiceType } from '../models/ServiceType.ts';

// 跟 apiClient（src/api/client.tsx）共用同一個環境變數，網域會隨環境（本地/alpha/正式站）自動切換，
// 不要另外寫死字串——正式站的實際值是在 Vercel 後台針對 production 環境注入的，不會出現在 repo 裡的 .env。
export const JUPITER_BASE_URL = import.meta.env.VITE_BASE_URL;
const JUPITER_HOSTNAME = new URL(JUPITER_BASE_URL).hostname;

const STORE_TYPE_TO_PATH: Record<StoreIdType, string> = {
  PARKINGLOT: 'parkinglots',
  GAS_STATION: 'gas_stations',
  CHAIN_STORE: 'chain_stores',
};

const PATH_TO_STORE_TYPE: Record<string, StoreIdType> = Object.fromEntries(
  Object.entries(STORE_TYPE_TO_PATH).map(([type, path]) => [
    path,
    type as StoreIdType,
  ]),
);

// 依服務類型限制允許綁定的 storeId 型別：fueling 只能是 gas_stations、parking 只能是
// parkinglots，其餘（charging/maintenance/inspection）只能是 chain_stores。
const ALLOWED_STORE_TYPES_BY_SERVICE: Record<ServiceType, StoreIdType[]> = {
  PARKING: ['PARKINGLOT'],
  FUELING: ['GAS_STATION'],
  CHARGING: ['CHAIN_STORE'],
  MAINTENANCE: ['CHAIN_STORE'],
  INSPECTION: ['CHAIN_STORE'],
};

const UUID_REGEX =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * 由 storeIds 的 type 與 value 組出「前往商店」的編輯頁超連結。
 * e.g. https://jupiter.autopass.xyz/parkinglots/{value}/edit
 */
export function buildStoreUrl(entry: StoreIdEntry): string {
  return `${JUPITER_BASE_URL}/${STORE_TYPE_TO_PATH[entry.type]}/${entry.value}/edit`;
}

/**
 * 解析使用者貼上的舊後台(jupiter)商店網址，格式為
 * https://jupiter.autopass.xyz/{type}/{id}/... （... 之後的路徑不固定，忽略）。
 * 網域、type、id 三者缺一不可；type 必須跟這個服務類型允許綁定的 storeId 型別相符，
 * 否則丟出錯誤（呼叫端應該用 try/catch 接住，顯示錯誤訊息）。
 */
export function parseStoreUrl(
  url: string,
  serviceType: ServiceType,
): StoreIdEntry {
  let parsed: URL;
  try {
    parsed = new URL(url.trim());
  } catch {
    throw new Error('網址格式不正確');
  }

  if (parsed.hostname !== JUPITER_HOSTNAME) {
    throw new Error(`網址網域必須是 ${JUPITER_HOSTNAME}`);
  }

  const [pathType, id] = parsed.pathname.split('/').filter(Boolean);

  if (!pathType || !(pathType in PATH_TO_STORE_TYPE)) {
    throw new Error('無法從網址判斷商店類型');
  }
  if (!id || !UUID_REGEX.test(id)) {
    throw new Error('無法從網址判斷商店 ID');
  }

  const storeIdType = PATH_TO_STORE_TYPE[pathType];
  const allowedTypes = ALLOWED_STORE_TYPES_BY_SERVICE[serviceType];
  if (!allowedTypes.includes(storeIdType)) {
    const allowedPaths = allowedTypes
      .map((type) => STORE_TYPE_TO_PATH[type])
      .join(' / ');
    throw new Error(
      `這個服務類型只能綁定 ${allowedPaths} 的商店網址，收到的是 ${pathType}`,
    );
  }

  return { type: storeIdType, value: id };
}

/**
 * 比對表單裡貼的網址跟目前已綁定的 storeIds，決定這次提交要不要送出更新：
 * - 網址是空字串且目前也沒綁定 → 回傳 null（沒有變化，不用呼叫 API）
 * - 網址是空字串但目前有綁定 → 回傳 []（解除綁定）
 * - 網址解析出來的 type/value 跟目前綁定的完全相同 → 回傳 null（沒有變化）
 * - 其餘（新綁定或換成別的商店）→ 回傳 [解析出來的 entry]
 * 網址格式錯誤時直接讓 parseStoreUrl 的例外往上丟，呼叫端要用 try/catch 接住。
 */
export function resolveStoreIdsChange(
  url: string,
  serviceType: ServiceType,
  currentStoreIds: StoreIdEntry[],
): StoreIdEntry[] | null {
  const trimmed = url.trim();
  const current = currentStoreIds[0];

  if (!trimmed) {
    return current ? [] : null;
  }

  const entry = parseStoreUrl(trimmed, serviceType);
  if (current && current.type === entry.type && current.value === entry.value) {
    return null;
  }
  return [entry];
}
